import { z } from "zod";
import { defineAd } from "../core/ad-definition";
import { adlandAPI } from "../services/adland.api";
import { castImage } from "../types";

/**
 * Cast Ad Definition
 * Represents a Farcaster cast ad with validation and verification
 *
 * The metadata is deliberately the same shape as an X post's — display name,
 * handle, avatar, text, time — because the two render as the same card. A post
 * is a post; which network it was written on is a badge, not a layout.
 */
export const castAd = defineAd({
  type: "cast",
  data: z.object({
    hash: z.string().nonempty("Hash is required"),
  }),
  metadata: z.object({
    /** The author's chosen name, which is what a reader recognises. */
    displayName: z.string().optional(),
    username: z.string().optional(),
    pfpUrl: z.string().optional(),
    text: z.string(),
    /** Epoch milliseconds, as a string. */
    timestamp: z.string(),
    /**
     * A still of the first attachment, where the cast has one.
     *
     * Named and shaped exactly as the X post's, since the two render through
     * one card — the comment above about the metadata being deliberately the
     * same shape was true of every field but this one, and the card had a place
     * to draw a picture that a cast could never fill.
     */
    image: z.string().optional(),
  }),
  async verify({ hash }) {
    if (!/^0x[0-9a-fA-F]{40}$/.test(hash)) {
      throw new Error("Invalid Farcaster cast hash");
    }

    const res = await adlandAPI.verify.cast({ hash });
    if (!res.verified) {
      throw new Error("Cast hash verification failed");
    }
  },
  async getMetadata({ hash }) {
    const { cast } = await adlandAPI.metadata.cast({ hash });
    return {
      displayName: cast.author.displayName,
      username: cast.author.username,
      pfpUrl: cast.author.pfp?.url,
      text: cast.text,
      timestamp: String(cast.timestamp),
      image: castImage(cast) ?? undefined,
    };
  },
});

/**
 * Type inference for CastAd data
 */
export type CastAdData = z.infer<typeof castAd.data>;

/**
 * Type inference for CastAd metadata
 */
export type CastAdMetadata = z.infer<NonNullable<typeof castAd.metadata>>;

export type CastAd = {
  type: "cast";
  data: CastAdData;
  metadata: CastAdMetadata;
};
